import React, { useRef, useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { FaStar } from 'react-icons/fa'
import Confetti from 'react-confetti'
import { fetchGameResults } from '../actions/games';
import { connect } from 'react-redux'

const formatTime = (totalSeconds) => {
  if (totalSeconds == null || isNaN(totalSeconds)) return '–';
  const secs = Math.floor(Number(totalSeconds));
  const minutes = Math.floor(secs / 60);
  const seconds = secs % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const getRankIcon = (rank) => {
  const styles = {
    fontSize: 64,
    marginBottom: 12,
    animation: 'pulseGlow 2s infinite',
    filter: 'drop-shadow(0 0 6px rgba(255,255,255,0.7))'
  }
  switch (rank) {
    case 1: return <span style={styles}>🥇</span>
    case 2: return <span style={styles}>🥈</span>
    case 3: return <span style={styles}>🥉</span>
    default: return <span style={styles}>🎖</span>
  }
}

const PodiumBlock = ({ rank, contestant, height, gradient }) => {
  const blockStyle = {
    height,
    width: 240,
    background: gradient,
    borderRadius: '30px 30px 0 0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
    position: 'relative',
    animation: 'float 3s ease-in-out infinite alternate',
    padding: 20
  }

  const timeStyle = {
    fontSize: rank === 1 ? 58 : 42,
    color: 'white',
    fontWeight: 'bold',
    marginTop: 8
  }

  return (
    <div style={{ textAlign: 'center', position: 'relative' }}>
      <motion.div
        animate={{ y: [0, -10, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        style={{ fontSize: 28, fontWeight: 'bold', color: 'white', marginBottom: 20 }}
      >
        {contestant.name}
      </motion.div>
      <motion.div
        initial={{ scale: 0.8 }}
        animate={{ scale: 1 }}
        transition={{ duration: 0.5 }}
        style={blockStyle}
      >
        {getRankIcon(rank)}
        <div style={timeStyle}>{formatTime(contestant.time)}</div>
      </motion.div>
    </div>
  )
}

const Podium = ({ first, second, third }) => {
  const podiumRef = useRef()
  const [dimensions, setDimensions] = useState({ width: 800, height: 500 })

  useEffect(() => {
    if (podiumRef.current) {
      const rect = podiumRef.current.getBoundingClientRect()
      setDimensions({
        width: rect.width,
        height: rect.height,
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX
      })
    }
  }, [])

  return (
    <div
      ref={podiumRef}
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        height: '100%',
        minHeight: 500
      }}
    >
      {/* <div style={{ position: 'absolute', top: 0, left: 0, width: dimensions.width, height: dimensions.height, pointerEvents: 'none', zIndex: 1 }}>
        <Confetti numberOfPieces={250} recycle={true} width={dimensions.width} height={dimensions.height} />
      </div> */}
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end', gap: 60, position: 'relative', zIndex: 2 }}>
        <PodiumBlock rank={2} contestant={second} height={260} gradient="linear-gradient(135deg, #c9d6ff, #e2e2e2)" />
        <PodiumBlock rank={1} contestant={first} height={340} gradient="linear-gradient(135deg, #f6e27a, #f8d347)" />
        <PodiumBlock rank={3} contestant={third} height={220} gradient="linear-gradient(135deg, #f4b183, #d69056)" />
      </div>
      <div style={{
        marginTop: 60,
        textAlign: 'center',
        color: '#fff',
        textShadow: '0 0 10px #0ca2fc, 0 0 4px #0ca2fc'
      }}>
        <div style={{ fontSize: 85, fontWeight: 'bold', marginBottom: 8 }}>⏱️ All-Time Record</div>
        <div style={{ fontSize: 100, fontWeight: 'bold' }}>{formatTime(first.time)}</div>
      </div>
    </div>
  )
}

const LeaderboardRow = ({ contestant, index }) => {
  const isTopEight = index < 8
  const backgroundColor = isTopEight
    ? '#01304b'
    : 'linear-gradient(90deg, #01497c, #057dcd)'

  return (
    <motion.div
      initial={{ x: -20, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ delay: index * 0.03 }}
      style={{
        background: backgroundColor,
        color: 'white',
        padding: '20px 28px',
        marginBottom: 10,
        borderRadius: 12,
        boxShadow: '0 0 14px rgba(12,162,252,0.3)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: 18,
        transition: 'all 0.3s ease-in-out'
      }}
    >
      <div style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 10 }}>
        <motion.div
          animate={{ rotate: [0, 10, -10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          style={{ fontSize: 24, color: '#0ca2fc' }}
        >
          <FaStar />
        </motion.div>
        <span style={{ fontSize: 22, color: '#aee7ff' }}>
          {index + 1}. {contestant.name}
        </span>
      </div>
      <div style={{ fontSize: 18 }}>{formatTime(contestant.time)}</div>
    </motion.div>
  )
}

class GameRankingView extends React.Component {
  constructor(props) {
    super(props)
    this.gameRef = React.createRef()
    this.state = {
      isFullscreen: false,
      countdown: 10,
      autoTried: false
    }
  }

  componentDidMount() {
    const { fetchGameResults } = this.props
    fetchGameResults()

    // Always start the countdown first
    this.startCountdown()

    // Try to re-enter fullscreen if the flag is set,
    // but DON’T rely on it (countdown is already running).
    const saved = localStorage.getItem('wasFullscreen') === 'true'
    if (saved) {
      this.enterFullScreen().catch(() => {
        // If blocked, the countdown keeps running
      })
    }

    document.addEventListener('fullscreenchange', this.handleFullscreenChange)
    window.addEventListener('beforeunload', this.clearWasFullscreenFlag)
  }

  componentWillUnmount() {
    if (this.refreshInterval) clearInterval(this.refreshInterval)
    if (this.countdownInterval) clearInterval(this.countdownInterval)
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange)
    window.removeEventListener('beforeunload', this.clearWasFullscreenFlag)
  }

  clearWasFullscreenFlag = () => {
    // Prevent “sticky” true if the app closes while still fullscreen
    localStorage.removeItem('wasFullscreen')
  }

  startCountdown = () => {
    if (this.countdownInterval) clearInterval(this.countdownInterval)
    this.setState({ countdown: 10 })
    this.countdownInterval = setInterval(() => {
      this.setState(prev => {
        const next = Math.max(prev.countdown - 1, 0)
        if (next === 0) {
          clearInterval(this.countdownInterval)
          this.countdownInterval = null
          this.autoEnterFullscreen()
        }
        return { countdown: next }
      })
    }, 1000)
  }

  autoEnterFullscreen = async () => {
    // Avoid repeated attempts; browsers often require a user gesture for fullscreen
    if (this.state.autoTried || document.fullscreenElement) return
    this.setState({ autoTried: true })
    try {
      await this.enterFullScreen()
    } catch (e) {
      // Silent fail if the browser blocks it; user can still click the button
      console.warn('Auto fullscreen was blocked by the browser:', e)
    }
  }

  handleFullscreenChange = () => {
    const isNowFullscreen = document.fullscreenElement != null
    this.setState({ isFullscreen: isNowFullscreen })

    // Stop countdown only when we actually got fullscreen
    if (isNowFullscreen && this.countdownInterval) {
      clearInterval(this.countdownInterval)
      this.countdownInterval = null
    }

    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
      this.refreshInterval = null
    }

    if (isNowFullscreen) {
      this.props.fetchGameResults()
      this.refreshInterval = setInterval(this.props.fetchGameResults, 60000)
      localStorage.setItem('wasFullscreen', 'true')   // set on success
    } else {
      localStorage.removeItem('wasFullscreen')        // clear on exit
      // optional: restart countdown when user exits fullscreen
      if (!this.countdownInterval) this.startCountdown()
    }
  }

  enterFullScreen = async () => {
    const el = this.gameRef.current
    let promise
    if (el.requestFullscreen) promise = el.requestFullscreen()
    else if (el.webkitRequestFullscreen) promise = el.webkitRequestFullscreen()
    else if (el.msRequestFullscreen) promise = el.msRequestFullscreen()
    // if the browser returns a promise, await it
    if (promise && typeof promise.then === 'function') await promise
    localStorage.setItem('wasFullscreen', 'true')
  }

  render() {
    const { isFullscreen, countdown } = this.state
    const { games, loading, error } = this.props

    if (loading || !games) return <div style={{ color: 'white', padding: 50 }}>Loading leaderboard...</div>
    if (error) return <div style={{ color: 'red', padding: 50 }}>{error}</div>

    const sortedGames = Array.isArray(games) ? [...games].sort((a, b) => a.time - b.time) : []
    const top3 = sortedGames.slice(0, 3)
    const nextFive = sortedGames.slice(3, 8)
    const others = sortedGames.slice(8)

    const defaultPlayer = { name: '---', time: 0 }

    return (
      <div ref={this.gameRef} style={{
        background: 'linear-gradient(to bottom right, #011832, #0ca2fc)',
        minHeight: '100vh',
        color: 'white',
        padding: 50,
        fontFamily: 'Segoe UI, sans-serif',
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}>
        <div style={{ position: 'absolute', top: 0, left: 0, width: 1920, height: 1080, pointerEvents: 'none', zIndex: 1 }}>
          <Confetti numberOfPieces={250} recycle={true} width={1920} height={1080} />
        </div>
        {!isFullscreen && (
          <button
            onClick={this.enterFullScreen}
            style={{
              position: 'absolute',
              top: 20,
              right: 20,
              padding: '10px 16px',
              background: '#0ca2fc',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              zIndex: 999
            }}
            aria-label="Enter fullscreen"
            title="Enter fullscreen"
          >
            🔲 Fullscreen{typeof countdown === 'number' ? ` (${countdown})` : ''}
          </button>
        )}

        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <h1 style={{ fontSize: 64, fontWeight: 'bold', color: '#aee7ff', textShadow: '0 0 20px #0ca2fc, 0 0 10px #057dcd' }}>
            🏆 VACWAY GAME RANKING 🏆
          </h1>
          <p style={{ fontSize: 22, color: '#bdeeff' }}>Fastest players of the day</p>
        </div>

        <div style={{ display: 'flex', gap: 40 }}>
          <div style={{ flex: 1 }}>
            <Podium
              first={top3[0] || defaultPlayer}
              second={top3[1] || defaultPlayer}
              third={top3[2] || defaultPlayer}
            />
          </div>
          <div style={{ flex: 1, maxHeight: 'calc(100vh - 200px)', overflowY: 'scroll', paddingRight: 10, scrollbarWidth: 'none' }}>
            <div style={{ marginBottom: 40 }}>
              {nextFive.length > 0 ? (
                nextFive.map((c, i) => (
                  <LeaderboardRow key={c._id || c.id || i} contestant={c} index={i + 3} />
                ))
              ) : (
                <div style={{ color: '#aee7ff', fontStyle: 'italic' }}>No players ranked yet.</div>
              )}
            </div>
            <div>
              {others.length > 0 && others.map((c, i) => (
                <LeaderboardRow key={c._id || c.id || i} contestant={c} index={i + 8} />
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }
}

// Redux bindings
const mapStateToProps = (state) => ({
  games: state.games.games,
  loading: state.games.loading,
  error: state.games.error
})

const mapDispatchToProps = {
  fetchGameResults
}

export default connect(mapStateToProps, mapDispatchToProps)(GameRankingView)

